import { authenticatedFetch } from "@/services/utils/authenticatedFetch";

export type BlobContent = {
  content: string | ArrayBuffer;
  contentType: string;
  fileName: string;
};

// Supported file types for text preview
const TEXT_FILE_EXTENSIONS = ['.txt', '.md', '.json', '.csv', '.xml', '.yaml', '.yml', '.log'];
const TEXT_CONTENT_TYPES = ['text/', 'application/json', 'application/xml'];

// DOCX file detection
const DOCX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOCX_EXTENSION = '.docx';

// PDF file detection
const PDF_CONTENT_TYPE = 'application/pdf';

/**
 * Downloads a blob from the server and returns its content with appropriate type handling
 * @param blobName - The name of the blob to download
 * @returns Promise<BlobContent> - The blob content with metadata
 */
export default async function downloadBlob(blobName: string): Promise<BlobContent> {
  const url = `/api/blob`;

  const response = await authenticatedFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      blob_name: blobName,
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to download blob: ${response.status} ${response.statusText}`);
  }

  const contentType = response.headers.get('content-type') || 'application/octet-stream';
  let content: string | ArrayBuffer;

  // Determine content handling strategy based on file type
  if (isTextFile(contentType, blobName)) {
    content = await response.text();
  } else if (isPdfFile(contentType)) {
    // For PDFs, create a blob URL for iframe display
    const blob = await response.blob();
    content = URL.createObjectURL(blob);
  } else if (isDocxFile(contentType, blobName)) {
    // For DOCX files, return ArrayBuffer for mammoth processing
    content = await response.arrayBuffer();
  } else {
    // For other file types, try text first, fallback to blob URL
    try {
      content = await response.text();
    } catch {
      // If text parsing fails, create a blob URL for download
      const blob = await response.blob();
      content = URL.createObjectURL(blob);
    }
  }

  return {
    content,
    contentType,
    fileName: blobName,
  };
}

/**
 * Checks if a file should be treated as text based on content type and extension
 */
function isTextFile(contentType: string, fileName: string): boolean {
  // Check content type
  if (TEXT_CONTENT_TYPES.some(type => contentType.startsWith(type))) {
    return true;
  }
  
  // Check file extension
  const lowerFileName = fileName.toLowerCase();
  return TEXT_FILE_EXTENSIONS.some(ext => lowerFileName.endsWith(ext));
}

/**
 * Checks if a file is a PDF
 */
function isPdfFile(contentType: string): boolean {
  return contentType === PDF_CONTENT_TYPE;
}

/**
 * Checks if a file is a DOCX document
 */
function isDocxFile(contentType: string, fileName: string): boolean {
  return contentType === DOCX_CONTENT_TYPE || fileName.toLowerCase().endsWith(DOCX_EXTENSION);
}
